/**
 * NotebookTab - Onglet de notes dans la fiche du joueur
 * Distinction visuelle entre notes automatiques et manuelles
 * Recherche et formatage simple
 */

import React, {useState, useEffect, useRef} from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Platform,
  Modal,
  KeyboardAvoidingView,
  SafeAreaView,
} from 'react-native';
import Markdown from 'react-native-markdown-display';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {faSearch, faLightbulb, faSave, faTimes, faTrash, faPlus} from '@fortawesome/free-solid-svg-icons';
import {usePlayer} from '../contexts/PlayerContext';
import gameStateService from '../services/gameStateService';
import {Fonts} from '../config/fonts';
import {NotebookEntry} from '../models/NotebookEntry';

interface NotebookTabProps {
  isDarkMode?: boolean;
}

const NotebookTab: React.FC<NotebookTabProps> = ({isDarkMode = false}) => {
  const {gameState, setGameState} = usePlayer();
  const [searchQuery, setSearchQuery] = useState('');
  const [editingContent, setEditingContent] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<NotebookEntry | null>(null);
  const textInputRef = useRef<TextInput>(null);
  const scrollViewRef = useRef<ScrollView>(null);
  const notesSectionRef = useRef<View>(null);

  // Migrer les données si nécessaire (au chargement)
  useEffect(() => {
    if (gameState) {
      // Migration automatique gérée par gameLogicService.migrateNotebookData
      // Ici on s'assure juste que les tableaux existent
      if (!Array.isArray(gameState.data.notebookAuto)) {
        gameState.data.notebookAuto = [];
      }
      if (!Array.isArray(gameState.data.notebookManual)) {
        gameState.data.notebookManual = [];
      }
    }
  }, [gameState]);

  // Ouvrir la modale pour ajouter une nouvelle note manuelle
  const handleOpenEditModal = () => {
    setEditingContent('');
    setHasChanges(false);
    setShowEditModal(true);
    setTimeout(() => {
      textInputRef.current?.focus();
    }, 100);
  };

  // Ouvrir la modale de confirmation de suppression
  const handleDeleteEntry = (entry: NotebookEntry) => {
    if (!gameState || entry.source !== 'manual') {
      return;
    }
    setNoteToDelete(entry);
    setShowDeleteModal(true);
  };

  // Confirmer la suppression
  const confirmDelete = async () => {
    if (!gameState || !noteToDelete) {
      return;
    }

    try {
      const updatedManual = (gameState.data.notebookManual || []).filter(e => e.id !== noteToDelete.id);
      const updatedGameState = {
        ...gameState,
        data: {
          ...gameState.data,
          notebookManual: updatedManual,
        },
        dirty: true,
      };

      const savedState = await gameStateService.updateGameState(updatedGameState);
      setGameState(savedState);
      setShowDeleteModal(false);
      setNoteToDelete(null);
    } catch (error) {
      console.error('[NotebookTab] Error deleting manual note:', error);
      setShowDeleteModal(false);
      setNoteToDelete(null);
    }
  };

  // Annuler la suppression
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setNoteToDelete(null);
  };

  // Sauvegarder les notes manuelles (création uniquement, pas de modification)
  const handleSaveManual = async () => {
    if (!gameState || !editingContent.trim()) {
      return;
    }

    try {
      const currentManual = gameState.data.notebookManual || [];
      
      // Créer une nouvelle entrée uniquement
      const newEntry: NotebookEntry = {
        id: `manual-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        content: editingContent.trim(),
        source: 'manual',
        createdAt: new Date().toISOString(),
      };
      const updatedManual = [...currentManual, newEntry];

      const updatedGameState = {
        ...gameState,
        data: {
          ...gameState.data,
          notebookManual: updatedManual,
        },
        dirty: true,
      };

      const savedState = await gameStateService.updateGameState(updatedGameState);
      setGameState(savedState);
      setHasChanges(false);
      setShowEditModal(false);
      setEditingContent('');
    } catch (error) {
      console.error('[NotebookTab] Error saving manual note:', error);
    }
  };

  // Fermer la modale d'édition
  const handleCloseEditModal = () => {
    if (hasChanges) {
      // Sauvegarder automatiquement avant de fermer
      handleSaveManual();
    } else {
      setShowEditModal(false);
      setEditingContent('');
    }
  };

  // Fusionner les notes automatiques et manuelles pour l'affichage
  // Trier par createdAt pour l'ordre chronologique
  const autoNotes: NotebookEntry[] = Array.isArray(gameState?.data.notebookAuto) 
    ? gameState.data.notebookAuto 
    : [];
  const manualNotes: NotebookEntry[] = Array.isArray(gameState?.data.notebookManual) 
    ? gameState.data.notebookManual 
    : [];

  const allNotes: NotebookEntry[] = [...autoNotes, ...manualNotes].sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return dateA - dateB;
  });

  const filteredNotes = searchQuery.trim() 
    ? allNotes.filter(note => note.content.toLowerCase().includes(searchQuery.toLowerCase()))
    : allNotes;

  const hasAnyNotes = allNotes.length > 0;
  const hasManualNotes = manualNotes.length > 0;

  // Rendu markdown pour les notes
  const renderMarkdown = (text: string): React.ReactNode => {
    if (!text.trim()) {
      return null;
    }

    const markdownStyles = {
      body: {
        color: isDarkMode ? '#F5F3ED' : '#2C3E50',
        fontFamily: Fonts.reading,
        fontSize: 14,
        lineHeight: 20,
      },
      paragraph: {
        marginBottom: 8,
      },
      list_item: {
        marginBottom: 4,
      },
      bullet_list: {
        marginBottom: 8,
      },
      ordered_list: {
        marginBottom: 8,
      },
      strong: {
        fontWeight: '600',
        color: isDarkMode ? '#F5F3ED' : '#2C3E50',
      },
      em: {
        fontStyle: 'italic',
      },
      code_inline: {
        backgroundColor: isDarkMode ? '#3D4F63' : '#E8D5C4',
        paddingHorizontal: 4,
        paddingVertical: 2,
        borderRadius: 4,
        fontFamily: 'monospace',
        fontSize: 12,
      },
      code_block: {
        backgroundColor: isDarkMode ? '#2C2D30' : '#F5F3ED',
        padding: 12,
        borderRadius: 8,
        marginBottom: 8,
      },
      heading1: {
        fontSize: 20,
        fontWeight: '700',
        marginBottom: 8,
        marginTop: 12,
      },
      heading2: {
        fontSize: 18,
        fontWeight: '600',
        marginBottom: 6,
        marginTop: 10,
      },
      heading3: {
        fontSize: 16,
        fontWeight: '600',
        marginBottom: 4,
        marginTop: 8,
      },
      link: {
        color: '#8B7355',
        textDecorationLine: 'underline',
      },
      blockquote: {
        borderLeftWidth: 4,
        borderLeftColor: '#8B7355',
        paddingLeft: 12,
        marginLeft: 0,
        marginBottom: 8,
        backgroundColor: isDarkMode ? 'rgba(139, 115, 85, 0.1)' : 'rgba(139, 115, 85, 0.05)',
        paddingVertical: 8,
      },
    };

    return (
      <Markdown style={markdownStyles}>
        {text}
      </Markdown>
    );
  };

  return (
    <View style={styles.container}>
      {/* Barre de recherche */}
      <View style={[styles.searchContainer, isDarkMode && styles.searchContainerDark]}>
        <FontAwesomeIcon icon={faSearch} size={16} color={isDarkMode ? '#9CA3AF' : '#6B7280'} style={styles.searchIcon} />
        <TextInput
          style={[styles.searchInput, isDarkMode && styles.searchInputDark]}
          placeholder="Rechercher dans les notes..."
          placeholderTextColor={isDarkMode ? '#6B7280' : '#9CA3AF'}
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity
            onPress={() => setSearchQuery('')}
            style={styles.clearSearchButton}
            activeOpacity={0.7}>
            <FontAwesomeIcon icon={faTimes} size={14} color={isDarkMode ? '#9CA3AF' : '#6B7280'} />
          </TouchableOpacity>
        )}
      </View>

      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        nestedScrollEnabled={true}
        scrollEnabled={true}>
        {!hasAnyNotes && !searchQuery ? (
          <View style={[styles.emptyCard, isDarkMode && styles.emptyCardDark]}>
            <FontAwesomeIcon icon={faLightbulb} size={32} color={isDarkMode ? '#6B7280' : '#9CA3AF'} />
            <Text style={[styles.emptyText, isDarkMode && styles.emptyTextDark]}>
              Aucune note pour le moment
            </Text>
            <Text style={[styles.emptySubtext, isDarkMode && styles.emptySubtextDark]}>
              Les notes automatiques apparaîtront ici au fil de votre aventure
            </Text>
          </View>
        ) : (
          <View style={styles.section}>
            {/* Header avec bouton d'édition */}
            <View style={styles.sectionHeader}>
              <View style={styles.sectionHeaderLeft}>
                <FontAwesomeIcon icon={faLightbulb} size={16} color="#8B7355" />
                <Text style={[styles.sectionTitle, isDarkMode && styles.sectionTitleDark]}>
                  Mes notes
                </Text>
              </View>
              <TouchableOpacity
                style={styles.editButton}
                onPress={handleOpenEditModal}
                activeOpacity={0.7}>
                <FontAwesomeIcon icon={faPlus} size={14} color="#8B7355" />
                <Text style={styles.editButtonText}>
                  Ajouter
                </Text>
              </TouchableOpacity>
            </View>

            {/* Liste fusionnée des notes */}
            {filteredNotes.length > 0 ? (
              <View style={styles.notesList}>
                {filteredNotes.map((note) => (
                  <View 
                    key={note.id} 
                    style={[
                      styles.noteEntry, 
                      note.source === 'auto' && styles.noteEntryAuto,
                      isDarkMode && styles.noteEntryDark,
                      note.source === 'auto' && isDarkMode && styles.noteEntryAutoDark
                    ]}>
                    {note.source === 'auto' && (
                      <View style={[styles.autoBadgeInline, isDarkMode && styles.autoBadgeInlineDark]}>
                        <FontAwesomeIcon icon={faLightbulb} size={12} color={isDarkMode ? "#81C784" : "#2E7D32"} />
                        <Text style={[styles.autoBadgeInlineText, isDarkMode && styles.autoBadgeInlineTextDark]}>Auto</Text>
                      </View>
                    )}
                    <View style={styles.noteEntryContent}>
                      {note.source === 'auto' ? (
                        // Notes automatiques : support Markdown (viennent de l'éditeur)
                        renderMarkdown(note.content)
                      ) : (
                        // Notes manuelles : texte brut (pas de Markdown)
                        <Text style={[styles.noteText, isDarkMode && styles.noteTextDark]}>
                          {note.content}
                        </Text>
                      )}
                    </View>
                    {note.source === 'manual' && (
                      <View style={styles.noteEntryActions} pointerEvents="box-none">
                        <TouchableOpacity
                          style={styles.deleteEntryButton}
                          onPress={() => handleDeleteEntry(note)}
                          activeOpacity={0.7}
                          hitSlop={{top: 10, bottom: 10, left: 10, right: 10}}>
                          <FontAwesomeIcon icon={faTrash} size={14} color="#DC2626" />
                        </TouchableOpacity>
                      </View>
                    )}
                  </View>
                ))}
              </View>
            ) : (
              <View style={[styles.notesCard, isDarkMode && styles.notesCardDark]}>
                <Text style={[styles.noResultsText, isDarkMode && styles.noResultsTextDark]}>
                  Aucun résultat pour "{searchQuery}"
                </Text>
              </View>
            )}
          </View>
        )}
      </ScrollView>

      {/* Modale de confirmation de suppression */}
      <Modal
        visible={showDeleteModal}
        transparent
        animationType="fade"
        onRequestClose={cancelDelete}>
        <View style={styles.confirmModalOverlay}>
          <View style={[styles.confirmModalContent, isDarkMode && styles.confirmModalContentDark]}>
            <View style={styles.confirmModalHeader}>
              <FontAwesomeIcon icon={faTrash} size={24} color="#DC2626" />
              <Text style={[styles.confirmModalTitle, isDarkMode && styles.confirmModalTitleDark]}>
                Supprimer la note
              </Text>
            </View>
            
            <View style={styles.confirmModalBody}>
              <Text style={[styles.confirmModalText, isDarkMode && styles.confirmModalTextDark]}>
                Êtes-vous sûr de vouloir supprimer cette note ?
              </Text>
              {noteToDelete && (
                <View style={[styles.confirmModalNotePreview, isDarkMode && styles.confirmModalNotePreviewDark]}>
                  <Text style={[styles.confirmModalNoteText, isDarkMode && styles.confirmModalNoteTextDark]} numberOfLines={3}>
                    {noteToDelete.content}
                  </Text>
                </View>
              )}
            </View>

            <View style={styles.confirmModalFooter}>
              <TouchableOpacity
                style={[styles.confirmModalCancelButton, isDarkMode && styles.confirmModalCancelButtonDark]}
                onPress={cancelDelete}
                activeOpacity={0.7}>
                <Text style={[styles.confirmModalCancelText, isDarkMode && styles.confirmModalCancelTextDark]}>
                  Annuler
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.confirmModalDeleteButton}
                onPress={confirmDelete}
                activeOpacity={0.7}>
                <FontAwesomeIcon icon={faTrash} size={16} color="#fff" style={{marginRight: 6}} />
                <Text style={styles.confirmModalDeleteText}>
                  Supprimer
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Modale d'édition des notes */}
      <Modal
        visible={showEditModal}
        transparent
        animationType="slide"
        onRequestClose={handleCloseEditModal}>
        <KeyboardAvoidingView
          style={styles.modalContainer}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}>
          <SafeAreaView style={styles.modalSafeArea}>
            <View style={[styles.modalContent, isDarkMode && styles.modalContentDark]}>
              {/* Header */}
              <View style={[styles.modalHeader, isDarkMode && styles.modalHeaderDark]}>
                <View style={styles.modalHeaderLeft}>
                  <FontAwesomeIcon icon={faPlus} size={24} color="#8B7355" style={styles.modalHeaderIcon} />
                  <Text style={[styles.modalTitle, isDarkMode && styles.modalTitleDark]}>
                    Ajouter une note
                  </Text>
                </View>
                <TouchableOpacity 
                  style={styles.modalCloseButton} 
                  onPress={handleCloseEditModal}
                  activeOpacity={0.7}>
                  <FontAwesomeIcon icon={faTimes} size={24} color="#8B7355" />
                </TouchableOpacity>
              </View>

              {/* Content */}
              <View style={styles.modalBody}>
                <View style={styles.modalInputContainer}>
                  <Text style={[styles.modalInputLabel, isDarkMode && styles.modalInputLabelDark]}>
                    Ajoutez vos notes personnelles ici
                  </Text>
                  <TextInput
                    ref={textInputRef}
                    style={[styles.modalInput, isDarkMode && styles.modalInputDark]}
                    multiline
                    placeholder="Ajoutez vos notes personnelles ici..."
                    placeholderTextColor={isDarkMode ? '#6B7280' : '#9CA3AF'}
                    value={editingContent}
                    onChangeText={(text) => {
                      if (text.length <= 4000) {
                        setEditingContent(text);
                        setHasChanges(true);
                      }
                    }}
                    textAlignVertical="top"
                    maxLength={4000}
                  />
                  <Text style={[styles.modalCharCount, isDarkMode && styles.modalCharCountDark]}>
                    {editingContent.length} / 4000 caractères
                  </Text>
                </View>
              </View>

              {/* Footer */}
              <View style={[styles.modalFooter, isDarkMode && styles.modalFooterDark]}>
                <TouchableOpacity
                  style={[styles.modalCancelButton, isDarkMode && styles.modalCancelButtonDark]}
                  onPress={() => {
                    setEditingContent('');
                    setHasChanges(false);
                    setShowEditModal(false);
                  }}
                  activeOpacity={0.7}>
                  <Text style={[styles.modalCancelButtonText, isDarkMode && styles.modalCancelButtonTextDark]}>
                    Annuler
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.modalSaveButton, !hasChanges && styles.modalSaveButtonDisabled]}
                  onPress={handleSaveManual}
                  disabled={!hasChanges}
                  activeOpacity={0.7}>
                  <FontAwesomeIcon icon={faSave} size={16} color="#fff" style={styles.modalSaveIcon} />
                  <Text style={styles.modalSaveButtonText}>Sauvegarder</Text>
                </TouchableOpacity>
              </View>
            </View>
          </SafeAreaView>
        </KeyboardAvoidingView>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'relative',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginHorizontal: 20,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#E8D5C4',
  },
  searchContainerDark: {
    backgroundColor: '#2C2D30',
    borderColor: '#3D4F63',
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: '#2C3E50',
    fontFamily: Fonts.ui,
  },
  searchInputDark: {
    color: '#F5F3ED',
  },
  clearSearchButton: {
    padding: 4,
  },
  scrollView: {
    flex: 1,
    marginBottom: 0,
  },
  scrollContent: {
    paddingBottom: 32,
  },
  section: {
    marginBottom: 24,
    paddingHorizontal: 20,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  sectionHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2C3E50',
    fontFamily: Fonts.title,
    marginLeft: 8,
  },
  sectionTitleDark: {
    color: '#F5F3ED',
  },
  autoBadge: {
    backgroundColor: '#E8F5E9',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  autoBadgeDark: {
    backgroundColor: '#2E7D32',
  },
  badgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#2E7D32',
    fontFamily: Fonts.ui,
  },
  notesCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E8D5C4',
    ...Platform.select({
      web: {
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 1},
        shadowOpacity: 0.05,
        shadowRadius: 3,
        elevation: 1,
      },
    }),
  },
  notesCardDark: {
    backgroundColor: '#2C2D30',
    borderColor: '#3D4F63',
  },
  notesContent: {
    gap: 8,
  },
  notesList: {
    gap: 12,
  },
  noteEntry: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E8D5C4',
    ...Platform.select({
      web: {
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 1},
        shadowOpacity: 0.05,
        shadowRadius: 3,
        elevation: 1,
      },
    }),
  },
  noteEntryAuto: {
    backgroundColor: '#F0F9F0',
    borderColor: '#C8E6C9',
    borderLeftWidth: 4,
    borderLeftColor: '#4CAF50',
  },
  noteEntryDark: {
    backgroundColor: '#2C2D30',
    borderColor: '#3D4F63',
  },
  noteEntryAutoDark: {
    backgroundColor: '#1B3A1F',
    borderColor: '#2E7D32',
    borderLeftColor: '#4CAF50',
  },
  autoBadgeInline: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: '#E8F5E9',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
    marginBottom: 8,
    gap: 4,
  },
  autoBadgeInlineDark: {
    backgroundColor: '#2E7D32',
  },
  autoBadgeInlineText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#2E7D32',
    fontFamily: Fonts.ui,
  },
  autoBadgeInlineTextDark: {
    color: '#81C784',
  },
  noteEntryContent: {
    flex: 1,
  },
  noteEntryActions: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
    alignSelf: 'flex-end',
  },
  deleteEntryButton: {
    padding: 6,
    borderRadius: 6,
    backgroundColor: 'rgba(220, 38, 38, 0.1)',
  },
  noteLine: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  listBullet: {
    fontSize: 16,
    color: '#8B7355',
    marginRight: 8,
    fontFamily: Fonts.reading,
  },
  noteText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
    color: '#2C3E50',
    fontFamily: Fonts.reading,
  },
  noteTextDark: {
    color: '#F5F3ED',
  },
  manualInput: {
    minHeight: 150,
    fontSize: 14,
    color: '#2C3E50',
    fontFamily: Fonts.reading,
    ...(Platform.OS !== 'web' && {
      textAlignVertical: 'top',
    }),
  },
  manualInputDark: {
    color: '#F5F3ED',
  },
  editActions: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#E8D5C4',
  },
  charCount: {
    fontSize: 12,
    color: '#9CA3AF',
    textAlign: 'right',
    marginBottom: 8,
    fontFamily: Fonts.ui,
  },
  charCountDark: {
    color: '#6B7280',
  },
  editButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
  },
  cancelButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E8D5C4',
    backgroundColor: '#fff',
  },
  cancelButtonDark: {
    backgroundColor: '#2C2D30',
    borderColor: '#3D4F63',
  },
  cancelButtonText: {
    fontSize: 14,
    color: '#8B7355',
    fontFamily: Fonts.ui,
    fontWeight: '500',
  },
  cancelButtonTextDark: {
    color: '#D4C4B0',
  },
  saveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    backgroundColor: '#8B7355',
    gap: 6,
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonText: {
    fontSize: 14,
    color: '#fff',
    fontFamily: Fonts.ui,
    fontWeight: '600',
  },
  editButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: '#F5F3ED',
    borderWidth: 1,
    borderColor: '#E8D5C4',
    gap: 6,
  },
  editButtonText: {
    fontSize: 12,
    color: '#8B7355',
    fontFamily: Fonts.ui,
    fontWeight: '500',
  },
  emptyCard: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    marginHorizontal: 20,
    backgroundColor: '#fff',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#E8D5C4',
    borderStyle: 'dashed',
  },
  emptyCardDark: {
    backgroundColor: '#2C2D30',
    borderColor: '#3D4F63',
  },
  emptyText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#6B7280',
    fontFamily: Fonts.title,
    marginTop: 12,
  },
  emptyTextDark: {
    color: '#9CA3AF',
  },
  emptySubtext: {
    fontSize: 14,
    color: '#9CA3AF',
    fontFamily: Fonts.reading,
    marginTop: 8,
    textAlign: 'center',
  },
  emptySubtextDark: {
    color: '#6B7280',
  },
  emptyNotesText: {
    fontSize: 14,
    fontStyle: 'italic',
    color: '#9CA3AF',
    fontFamily: Fonts.reading,
    textAlign: 'center',
    padding: 20,
  },
  emptyNotesTextDark: {
    color: '#6B7280',
  },
  noResultsText: {
    fontSize: 14,
    fontStyle: 'italic',
    color: '#9CA3AF',
    fontFamily: Fonts.reading,
    textAlign: 'center',
    padding: 20,
  },
  noResultsTextDark: {
    color: '#6B7280',
  },
  // Styles pour la modale
  modalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalSafeArea: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#F5F3ED',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '90%',
    ...Platform.select({
      web: {
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.3)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {width: 0, height: -2},
        shadowOpacity: 0.25,
        shadowRadius: 3.84,
        elevation: 5,
      },
    }),
  },
  modalContentDark: {
    backgroundColor: '#1E1F22',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E8D5C4',
    backgroundColor: '#fff',
  },
  modalHeaderDark: {
    backgroundColor: '#2C2D30',
    borderBottomColor: '#3D4F63',
  },
  modalHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  modalHeaderIcon: {
    marginRight: 12,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#8B7355',
    fontFamily: Fonts.title,
  },
  modalTitleDark: {
    color: '#D4C4B0',
  },
  modalCloseButton: {
    padding: 8,
  },
  modalBody: {
    padding: 16,
    flex: 1,
  },
  modalInput: {
    minHeight: 300,
    fontSize: 16,
    color: '#333',
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E8D5C4',
    fontFamily: Fonts.reading,
    ...(Platform.OS !== 'web' && {
      textAlignVertical: 'top',
    }),
  },
  modalInputDark: {
    backgroundColor: '#2C2D30',
    borderColor: '#3D4F63',
    color: '#F5F3ED',
  },
  modalCharCount: {
    fontSize: 12,
    color: '#999',
    textAlign: 'right',
    marginTop: 8,
    fontFamily: Fonts.ui,
  },
  modalCharCountDark: {
    color: '#6B7280',
  },
  modalPreviewContainer: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#E8D5C4',
  },
  modalPreviewLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2C3E50',
    marginBottom: 8,
    fontFamily: Fonts.title,
  },
  modalPreviewLabelDark: {
    color: '#F5F3ED',
  },
  modalPreview: {
    backgroundColor: '#F5F3ED',
    borderRadius: 8,
    padding: 12,
    minHeight: 100,
  },
  modalPreviewDark: {
    backgroundColor: '#2C2D30',
  },
  modalFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#E8D5C4',
    backgroundColor: '#fff',
    gap: 12,
  },
  modalFooterDark: {
    backgroundColor: '#2C2D30',
    borderTopColor: '#3D4F63',
  },
  modalCancelButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E8D5C4',
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalCancelButtonDark: {
    backgroundColor: '#2C2D30',
    borderColor: '#3D4F63',
  },
  modalCancelButtonText: {
    fontSize: 16,
    color: '#8B7355',
    fontFamily: Fonts.ui,
    fontWeight: '500',
  },
  modalCancelButtonTextDark: {
    color: '#D4C4B0',
  },
  modalSaveButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#8B7355',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    gap: 8,
  },
  modalSaveButtonDisabled: {
    opacity: 0.5,
  },
  modalSaveIcon: {
    marginRight: 0,
  },
  modalSaveButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    fontFamily: Fonts.ui,
  },
  // Styles pour la modale de confirmation de suppression
  confirmModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  confirmModalContent: {
    backgroundColor: '#F5F3ED',
    borderRadius: 16,
    width: '100%',
    maxWidth: 400,
    ...Platform.select({
      web: {
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 4},
        shadowOpacity: 0.3,
        shadowRadius: 8,
        elevation: 8,
      },
    }),
  },
  confirmModalContentDark: {
    backgroundColor: '#2C2D30',
  },
  confirmModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 20,
    paddingBottom: 16,
    gap: 12,
  },
  confirmModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#2C3E50',
    fontFamily: Fonts.title,
  },
  confirmModalTitleDark: {
    color: '#F5F3ED',
  },
  confirmModalBody: {
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  confirmModalText: {
    fontSize: 15,
    color: '#2C3E50',
    fontFamily: Fonts.reading,
    marginBottom: 12,
    lineHeight: 22,
  },
  confirmModalTextDark: {
    color: '#F5F3ED',
  },
  confirmModalNotePreview: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#E8D5C4',
  },
  confirmModalNotePreviewDark: {
    backgroundColor: '#1E1F22',
    borderColor: '#3D4F63',
  },
  confirmModalNoteText: {
    fontSize: 14,
    color: '#6B7280',
    fontFamily: Fonts.reading,
    fontStyle: 'italic',
    lineHeight: 20,
  },
  confirmModalNoteTextDark: {
    color: '#9CA3AF',
  },
  confirmModalFooter: {
    flexDirection: 'row',
    padding: 20,
    paddingTop: 16,
    gap: 12,
    borderTopWidth: 1,
    borderTopColor: '#E8D5C4',
  },
  confirmModalCancelButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E8D5C4',
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  confirmModalCancelButtonDark: {
    backgroundColor: '#2C2D30',
    borderColor: '#3D4F63',
  },
  confirmModalCancelText: {
    fontSize: 15,
    color: '#8B7355',
    fontFamily: Fonts.ui,
    fontWeight: '500',
  },
  confirmModalCancelTextDark: {
    color: '#D4C4B0',
  },
  confirmModalDeleteButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#DC2626',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  confirmModalDeleteText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
    fontFamily: Fonts.ui,
  },
});

export default NotebookTab;
